import * as React from "react";
import * as RechartsPrimitive from "recharts";
import type { BarProps } from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

// ---------------------------------------------------------------------------
// Drop-in replacement for your ChartContainer/BarChart block. Callers pass
// real segment values (e.g. percentages) and get back the exact same visual
// structure — same tooltip, same hidden axes, same stacked bars — with the
// min-width/gap/no-overflow math solved internally.
//
// Why the whole block, not just the Bars: the tooltip needs to display the
// REAL value (e.g. "47%"), but the layout needs a different, pre-solved
// pixel value to avoid overflow. Those two numbers are necessarily
// different, so something has to translate between them — this component
// does that translation once, internally, so every call site can stay
// ignorant of it and just pass real data in.
// ---------------------------------------------------------------------------

import { ChartContainer, ChartTooltip, type ChartConfig } from "./chart";

const CHART_BAR_GAP = 4;
const CHART_BAR_RADIUS = 4;
const CHART_BAR_MIN_WIDTH = 8;

type ChartBarShapeProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
};

/**
 * Pure cosmetic shape now: round the corners and inset by half the gap on
 * each side. It no longer tries to enforce CHART_BAR_MIN_WIDTH itself.
 *
 * Why the clamp got removed: this function only ever sees its own bar's
 * x/width — it has no idea what the container width is or what the other
 * stacked segments look like. `Math.max(width - gap, minWidth)` can only
 * grow a bar, never shrink a sibling to compensate, so enforcing the
 * minimum here is exactly what was causing the row to overflow the
 * container. The minimum now gets enforced upstream, in the chart's data,
 * before Recharts ever computes a layout — see GappedStackedProportionChart
 * below. By the time this shape function runs, `width` is already
 * guaranteed to be CHART_BAR_MIN_WIDTH + CHART_BAR_GAP or more, so a plain
 * inset is safe.
 */
function chartGappedRoundedBarShape(props: ChartBarShapeProps) {
  const { x = 0, y = 0, width = 0, height = 0, fill } = props;
  const barWidth = width > 0 ? Math.max(width - CHART_BAR_GAP, 0) : 0;

  return (
    <rect
      x={x + CHART_BAR_GAP / 2}
      y={y}
      width={barWidth}
      height={height}
      rx={`${CHART_BAR_RADIUS}px`}
      ry={`${CHART_BAR_RADIUS}px`}
      fill={fill}
    />
  );
}

function GappedRoundedChartBar(props: Omit<BarProps, "ref" | "shape">) {
  return (
    <RechartsPrimitive.Bar shape={chartGappedRoundedBarShape} {...props} />
  );
}
GappedRoundedChartBar.displayName = RechartsPrimitive.Bar.displayName;
GappedRoundedChartBar.defaultProps = RechartsPrimitive.Bar.defaultProps;
GappedRoundedChartBar.getComposedData = RechartsPrimitive.Bar.getComposedData;

// ---------------------------------------------------------------------------
// New: the piece that actually enforces min-width without ever overflowing.
// ---------------------------------------------------------------------------

/**
 * Clamp-and-redistribute: give every segment its naive proportional share of
 * `available` px, pin any segment below `minWidth` at `minWidth`, then
 * re-split what's left among the segments still unpinned. Repeat, because
 * shrinking the pool can push a previously-fine segment below the minimum
 * too. If there genuinely isn't room for every segment to hit minWidth,
 * scale all minimums down uniformly rather than overflow.
 */
function computeSegmentWidths(
  values: number[],
  available: number,
  minWidth: number
): number[] {
  const n = values.length;
  if (n === 0 || available <= 0) return values.map(() => 0);

  if (minWidth * n >= available) {
    const scale = available / (minWidth * n);
    return values.map(() => minWidth * scale);
  }

  const widths = new Array(n).fill(0);
  let remaining = available;
  let flexible = values.map((_, i) => i);

  let changed = true;
  while (changed) {
    changed = false;
    const flexTotal = flexible.reduce((sum, i) => sum + values[i], 0);
    if (flexTotal <= 0) break;

    const next = [...flexible].reduce(
      (state, i) => {
        const share = (values[i] / flexTotal) * state.remaining;
        if (share < minWidth) {
          widths[i] = minWidth;
          return {
            remaining: state.remaining - minWidth,
            flexible: state.flexible.filter((idx) => idx !== i),
            changed: true,
          };
        }
        return state;
      },
      { remaining, flexible, changed: false }
    );
    remaining = next.remaining;
    flexible = next.flexible;
    changed = next.changed;
  }

  const flexTotal = flexible.reduce((sum, i) => sum + values[i], 0);
  flexible.forEach((i) => {
    widths[i] = flexTotal > 0 ? (values[i] / flexTotal) * remaining : 0;
  });

  return widths;
}

export type GappedBarSegment = {
  key: string;
  value: number;
  color: string;
  label?: string;
};

type TooltipContentProp =
  | React.ReactElement
  | ((
      props: RechartsPrimitive.TooltipProps<ValueType, NameType>
    ) => React.ReactNode);

export function GappedStackedProportionChart({
  segments,
  className = "relative h-7 w-full",
  tooltipContent,
}: {
  segments: GappedBarSegment[];
  className?: string;
  /**
   * Tooltip content — a React element (e.g. <ChartTooltipContent />) or a
   * render function, like Recharts' Tooltip `content` prop. Automatically
   * receives the REAL segment values, not the internal pixel-width numbers.
   */
  tooltipContent: TooltipContentProp;
}) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = React.useState(0);

  React.useEffect(() => {
    const el = wrapperRef.current;
    const ro = new ResizeObserver((entries) => {
      setPlotWidth(entries[0]?.contentRect.width ?? 0);
    });
    if (el) {
      ro.observe(el);
    }
    return () => ro.disconnect();
  }, []);

  const visibleSegments = React.useMemo(
    () => segments.filter((s) => s.value > 0),
    [segments]
  );

  const rawValues = visibleSegments.map((s) => s.value);
  const rawValuesKey = rawValues.join(",");

  // n bars each inset by GAP/2 on both sides = (n-1) real gaps between bars
  // + a half-gap margin at each outer edge = n * GAP total reserved.
  const targetWidths = React.useMemo(
    () =>
      computeSegmentWidths(
        rawValues,
        plotWidth - CHART_BAR_GAP * visibleSegments.length,
        CHART_BAR_MIN_WIDTH
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawValuesKey, plotWidth, visibleSegments.length]
  );

  // Render-only dataset: pixel widths (+ one gap each) so Recharts' own
  // proportional stacking math lands exactly on our pre-solved widths.
  const renderData = React.useMemo(() => {
    const row: Record<string, number | string> = { name: "row" };
    visibleSegments.forEach((s, i) => {
      row[s.key] = targetWidths[i] + CHART_BAR_GAP;
    });
    return [row];
  }, [visibleSegments, targetWidths]);

  const config: ChartConfig = React.useMemo(() => {
    const cfg: ChartConfig = {};
    segments.forEach((s) => {
      cfg[s.key] = { label: s.label ?? s.key, color: s.color };
    });
    return cfg;
  }, [segments]);

  // Wraps whatever tooltip content the caller wants. Recharts always calls
  // this as a function (that's how its `content` prop works when given a
  // function), so this is the one place we can intercept the payload and
  // swap the internal pixel-width numbers back out for the real values —
  // meaning ANY tooltip content downstream, default or fully custom, just
  // sees correct data and never has to know the pixel hack exists.
  const patchedContent = React.useCallback(
    (props: RechartsPrimitive.TooltipProps<ValueType, NameType>) => {
      const payloadByKey = new Map(
        props.payload?.map((entry) => [entry.dataKey, entry])
      );
      const patchedPayload = segments.map((segment) => {
        const existing = payloadByKey.get(segment.key);
        return {
          ...existing,
          dataKey: segment.key,
          name: segment.label ?? segment.key,
          value: segment.value,
          color: segment.color,
          fill: segment.color,
          payload: { ...existing?.payload, fill: segment.color },
        };
      });

      if (typeof tooltipContent === "function") {
        const result = tooltipContent({ ...props, payload: patchedPayload });
        if (React.isValidElement(result)) {
          return React.cloneElement(result, {
            ...props,
            payload: patchedPayload,
          } as object);
        }
        return result;
      }
      if (React.isValidElement(tooltipContent)) {
        return React.cloneElement(tooltipContent, {
          ...props,
          payload: patchedPayload,
        } as object);
      }
      return null;
    },
    [segments, tooltipContent]
  );

  return (
    <div ref={wrapperRef} className={className}>
      {plotWidth > 0 && (
        <ChartContainer config={config} className="h-full w-full">
          <RechartsPrimitive.BarChart
            data={renderData}
            layout="vertical"
            width={plotWidth}
            barCategoryGap={0}
            barGap={0}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            <ChartTooltip cursor={false} content={patchedContent} />
            <RechartsPrimitive.XAxis
              type="number"
              hide
              domain={[0, plotWidth]}
            />
            <RechartsPrimitive.YAxis type="category" dataKey="name" hide />
            {visibleSegments.map((segment) => (
              <GappedRoundedChartBar
                key={segment.key}
                dataKey={segment.key}
                stackId="a"
                fill={segment.color}
                isAnimationActive={false}
              />
            ))}
          </RechartsPrimitive.BarChart>
        </ChartContainer>
      )}
    </div>
  );
}
